import { CardHeader, CardTitle, Skeleton } from '@folio/components';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@folio/components/ui/alert-dialog';
import { Badge } from '@folio/components/ui/badge';
import { Card, CardContent } from '@folio/components/ui/card';
import { cn } from '@folio/lib';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useDocumentUploadMode } from '@folio/utils/useDocumentUploadMode';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  keyValueFieldsToObject,
  policyControllerListPolicyDocumentsQueryOptions,
  policyDocumentExtractionControllerGetPolicyExtractionsQueryOptions,
  documentValidityControllerGetPolicyValidityQueryOptions,
  documentCoherenceControllerGetDocumentCoherenceQueryOptions,
  useDocumentExtractionControllerRetryDocumentExtraction,
  useDocumentExtractionControllerSubmitExtractionFeedback,
  type ListPolicyDocumentsOutput,
  type PolicyDocumentsDto,
  type GetDocumentExtractionOutput,
  type SubmitFeedbackBody,
} from 'korint-sdk';
import { useFeatureFlag, useKorintProductConfigSuspense } from 'korint-sdk/react';
import type { AIValidityResult } from 'korint-sdk/react';
import { DocumentsListsPolicyModule as LegacyDocumentsListsPolicyModule } from './legacy/DocumentsListsPolicyModule';
import type { DocumentCustomCategorieMapping } from '@folio/components/features/document-viewer';

import { DocumentsCollection } from '@folio/components/module-elements/Documents/DocumentsCollection';
import { FileDropzone } from '@folio/components/ui/file-dropzone/file-dropzone';
import {
  DocumentViewer,
  useDocumentViewer,
  AIClassificationBadge,
} from '@folio/components/features/document-viewer';
import { Button } from '@folio/components/ui/button';
import { ArchiveIcon, CheckIcon, FileText, XIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { documentsRegenSignalKey } from '@folio/utils/documentsRegenSignal';
import { useToast } from '@folio/components/ui/use-toast';
import { RenewalDocumentsCard } from './RenewalDocumentsCard';
import { useTranslation } from 'react-i18next';
import { sortBy } from 'lodash';
import { getDocumentCategoryLabel } from '@folio/utils/getDocumentCategoryLabel';
import { DOCUMENT_GROUPS, getDocumentGroup, getDocumentGroups } from './document-groups';
import type { ConformitySummary } from './conformity-summary';
import { Center, Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { ThemeIcon } from '@folio/components/ui/theme-icon';

type DisplayContext = 'policyholder' | 'broker';

export type DocumentConformityDecision = 'PENDING' | 'ACCEPTED' | 'REJECTED';
export interface DocumentConformityState {
  decision: DocumentConformityDecision;
  reason?: string;
  decidedByLabel?: string;
  decidedAutomatically?: boolean;
}

export interface DocumentsListsModuleProps {
  /**
   * Defines the context in which the module is displayed: 'policyholder' or 'broker'.
   * In the 'policyholder' context, we do not display the archived or renewal documents.
   * In the 'broker' context, we display the archived or renewal documents.
   */
  displayContext: DisplayContext;
  embedded?: boolean;
  showArchivedDocuments?: boolean;
  onShowArchivedDocumentsChange?: (showArchivedDocuments: boolean) => void;

  /**
   * Optionnal filter on displayed documents.
   */
  categories?: string[];

  /**
   * Optional categories to hide on top of the `categories` allowlist.
   */
  excludedCategories?: string[];

  /** Includes document types returned by the policy API even when absent from product config. */
  includeUnconfiguredDocuments?: boolean;

  /**
   * Extra actions to display in the module. This is an escape hatch to display components
   * that are not part of the module.
   */
  extraActions?: React.ReactNode;

  /**
   * If true, the module will poll until all documents in the documentsLists are loaded.
   * This is useful for Garantie Plus, where the documents are not immediately available.
   * We may want to have a more detailed polling system in the future where each document
   * is required or not.
   */
  pollUntilAllDocumentsAreLoaded?: boolean;

  /**
   * If true, the module will only display documents on the current branch.
   */
  onlyOnBranch?: boolean;

  conformityByDocumentId?: Map<string, DocumentConformityState>;
  onAcceptDocumentConformity?: (documentId: string) => void;
  onRejectDocumentConformity?: (documentId: string, reason: string) => void;
  onCancelDocumentConformity?: (documentId: string) => void;
  onAcceptAllDocumentConformity?: () => void;
  onReuploadDocumentConformity?: (documentId: string, file: File) => void;
}

const CATEGORY_TONES = [
  'bg-emerald-50 text-emerald-700',
  'bg-sky-50 text-sky-700',
  'bg-amber-50 text-amber-700',
  'bg-violet-50 text-violet-700',
  'bg-rose-50 text-rose-700',
];

function hashCategoryTone(category: string): string {
  let hash = 0;
  for (let i = 0; i < category.length; i++) hash = (hash * 31 + category.charCodeAt(i)) >>> 0;
  return CATEGORY_TONES[hash % CATEGORY_TONES.length];
}

export function DocumentCategorySwatch({ category }: { category: string }) {
  return (
    <div
      className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg ${hashCategoryTone(category)}`}
    >
      <FileText className="h-4 w-4" />
    </div>
  );
}

export function PolicyholderCompletionBanner() {
  const { t } = useTranslation();
  return (
    <Card className="mt-4 flex flex-col items-center gap-3 border-emerald-200 bg-emerald-50/40 py-8 text-center">
      <ThemeIcon size="xl" radius="full" tone="success">
        <CheckIcon />
      </ThemeIcon>
      <div>
        <Text size="md" weight="bold">
          {t('documentsListsPolicy.conformity.allDoneTitle')}
        </Text>
        <Text size="sm" tone="muted" className="mt-1">
          {t('documentsListsPolicy.conformity.allDoneBody')}
        </Text>
      </div>
    </Card>
  );
}

export function ConformityReadOnlyBadge({ conformity }: { conformity: DocumentConformityState }) {
  const { t } = useTranslation();

  if (conformity.decision === 'ACCEPTED') {
    return (
      <Badge variant="outline" className="gap-1 border-transparent bg-emerald-700 text-white">
        <CheckIcon className="h-3.5 w-3.5" />
        {t(
          conformity.decidedAutomatically
            ? 'documentsListsPolicy.conformity.acceptedAutomatically'
            : 'documentsListsPolicy.conformity.accepted',
        )}
      </Badge>
    );
  }
  if (conformity.decision === 'REJECTED') {
    return (
      <Badge variant="light" tone="destructive" className="gap-1">
        <XIcon className="h-3.5 w-3.5" />
        {t('documentsListsPolicy.conformity.rejected')}
      </Badge>
    );
  }
  return <Badge variant="secondary">{t('documentsListsPolicy.conformity.pending')}</Badge>;
}

export function ConformityReuploadZone({
  documentId,
  reason,
  onReupload,
}: {
  documentId: string;
  reason?: string;
  onReupload?: (documentId: string, file: File) => void | Promise<unknown>;
}) {
  const { t } = useTranslation();

  return (
    <div className="mt-2 px-2 pb-2">
      <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
        <strong>{t('documentsListsPolicy.conformity.rejectedReasonPrefix')}</strong> {reason}
      </div>
      {onReupload && (
        <FileDropzone
          multiple={false}
          accept={{
            'application/pdf': ['.pdf'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
          }}
          helpText={t('documentsListsPolicy.conformity.reuploadHint')}
          onDrop={async files => {
            const [file] = files;
            if (file) await onReupload(documentId, file);
          }}
        />
      )}
    </div>
  );
}

export function ConformityAcceptAllControl({
  conformityByDocumentId,
  summary,
  conformToAccept,
  onAcceptAll,
  variant = 'default',
}: {
  conformityByDocumentId?: Map<string, DocumentConformityState>;
  summary?: ConformitySummary;
  conformToAccept?: number;
  onAcceptAll?: () => void;
  variant?: 'default' | 'outline';
}) {
  const { t } = useTranslation();

  const {
    pending,
    accepted: acceptedDocuments,
    rejected,
    total: totalDocuments,
  } = useMemo(() => {
    let pending = 0;
    let accepted = 0;
    let rejected = 0;
    for (const { decision } of conformityByDocumentId?.values() ?? []) {
      if (decision === 'PENDING') pending++;
      else if (decision === 'ACCEPTED') accepted++;
      else if (decision === 'REJECTED') rejected++;
    }
    return { pending, accepted, rejected, total: conformityByDocumentId?.size ?? 0 };
  }, [conformityByDocumentId]);
  const accepted = summary?.accepted ?? acceptedDocuments;
  const total = summary?.total ?? totalDocuments;

  if (total === 0) return null;

  const action = conformToAccept === undefined ? 'acceptAll' : 'acceptConform';

  return (
    <Group gap={3}>
      {conformToAccept === undefined && (
        <Text inline size="sm" weight="medium" tone="muted" className="whitespace-nowrap">
          {t('documentsListsPolicy.conformity.summary', { accepted, total })}
        </Text>
      )}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant={variant}
            disabled={
              conformToAccept === undefined
                ? pending === 0 && rejected === 0
                : conformToAccept === 0
            }
          >
            <CheckIcon className="mr-1.5 h-4 w-4" />
            {t(`documentsListsPolicy.conformity.${action}`, { count: conformToAccept })}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`documentsListsPolicy.conformity.${action}ConfirmTitle`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {conformToAccept !== undefined
                ? t('documentsListsPolicy.conformity.acceptConformConfirmBody', {
                    count: conformToAccept,
                  })
                : rejected > 0
                  ? t('documentsListsPolicy.conformity.acceptAllConfirmBodyWithRejected', {
                      count: pending,
                      rejected,
                    })
                  : t('documentsListsPolicy.conformity.acceptAllConfirmBody', { count: pending })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={onAcceptAll}>
              {t(`documentsListsPolicy.conformity.${action}`, { count: conformToAccept })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Group>
  );
}

function selectDocumentsFromAPI(data: ListPolicyDocumentsOutput) {
  return data.data?.map(document => ({
    ...document,
    metadata: keyValueFieldsToObject(document.metadata),
  }));
}

function DocumentsListsPolicyModuleV2({
  displayContext,
  embedded = false,
  showArchivedDocuments: controlledShowArchivedDocuments,
  onShowArchivedDocumentsChange,
  categories,
  excludedCategories,
  includeUnconfiguredDocuments = false,
  extraActions,
  pollUntilAllDocumentsAreLoaded = false,
  onlyOnBranch = false,
  conformityByDocumentId,
  onAcceptDocumentConformity,
  onRejectDocumentConformity,
  onCancelDocumentConformity,
  onAcceptAllDocumentConformity,
  onReuploadDocumentConformity,
}: DocumentsListsModuleProps) {
  const { t } = useTranslation();
  const title = t('documentReview.contractDocuments');

  const { policy, workingBranch, branches } = usePolicyOnDashboard();
  const [internalShowArchivedDocuments, setInternalShowArchivedDocuments] = useState(false);
  const showArchivedDocuments = controlledShowArchivedDocuments ?? internalShowArchivedDocuments;
  const setShowArchivedDocuments =
    onShowArchivedDocumentsChange ?? setInternalShowArchivedDocuments;

  const productsConfig = useKorintProductConfigSuspense();
  const productConfig = productsConfig[policy.productId];
  const additionalDocumentTypes = productConfig?.additionalDocumentTypes ?? [];
  const generatedDocumentsDisplay = productConfig?.generatedDocumentsDisplay;

  const { data: documentConfigs = [] } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    select: ({ commonDocuments, generatedDocuments, nonDelegatedConfig, policyholderDocuments }) =>
      Array.from(
        new Set([
          ...(nonDelegatedConfig?.documents?.documentTypes ?? []),
          ...commonDocuments.map(({ type }) => type),
          ...generatedDocuments.map(({ documentType, configName }) => documentType ?? configName),
          ...additionalDocumentTypes,
          ...(displayContext === 'policyholder'
            ? (policyholderDocuments ?? []).map(({ category }) => category)
            : []),
        ]),
      ).filter(
        category =>
          (!categories || categories.includes(category)) &&
          !excludedCategories?.includes(category) &&
          category !== 'SEPA_MANDATE',
      ),
  });

  const uploadMode = useDocumentUploadMode('policyholderDocuments');

  // A funnel step (e.g. pricing) bumps this signal when it triggers regeneration of
  // generated documents. We then keep polling until a document newer than the one we
  // currently show arrives — otherwise the poll stops as soon as a *stale* document of
  // each type exists and the regenerated version is never picked up on funnel revisits.
  const { data: regenSignal } = useQuery<number>({
    queryKey: documentsRegenSignalKey(policy.id),
    queryFn: () => 0,
    enabled: false,
  });
  const awaitDocsNewerThanRef = useRef<string | null>(null);
  const lastHandledRegenSignalRef = useRef<number | undefined>(undefined);

  const {
    data: currentDocuments,
    status: currentDocumentsStatus,
    refetch: refetchCurrentDocuments,
  } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policy.id, {
      branchId: workingBranch?.id,
      onlyOnBranch,
    }),
    select: selectDocumentsFromAPI,
    placeholderData: keepPreviousData,
    refetchInterval: query => {
      if (query.state.dataUpdateCount > 10) return undefined;
      if (!query.state.data) return undefined;
      const currentDocuments = selectDocumentsFromAPI(query.state.data);

      const areAllDocumentsLoaded = documentConfigs.every(category =>
        currentDocuments?.some(d => d.category === category && d.metadata.scanresult === 'CLEAN'),
      );

      const awaitNewerThan = awaitDocsNewerThanRef.current;
      const regenSettled =
        !awaitNewerThan ||
        (currentDocuments ?? []).some(
          d =>
            d.metadata.scanresult === 'CLEAN' &&
            typeof d.metadata.whenat === 'string' &&
            d.metadata.whenat > awaitNewerThan,
        );

      // A pending regen polls on its own: bumpDocumentsRegenSignal is meant to work on any
      // screen, not only those opting into the "wait for every configured type" poll.
      const regenPending = Boolean(awaitNewerThan) && !regenSettled;

      return regenPending || (pollUntilAllDocumentsAreLoaded && !areAllDocumentsLoaded)
        ? 2000
        : undefined;
    },
  });

  useEffect(() => {
    if (regenSignal === undefined || lastHandledRegenSignalRef.current === regenSignal) return;
    // snapshot needs the list loaded, else the baseline is '' and the regen guard self-disables
    if (currentDocumentsStatus !== 'success') return;
    lastHandledRegenSignalRef.current = regenSignal;
    awaitDocsNewerThanRef.current = (currentDocuments ?? []).reduce((max, d) => {
      const whenAt = typeof d.metadata.whenat === 'string' ? d.metadata.whenat : '';
      return whenAt > max ? whenAt : max;
    }, '');
    refetchCurrentDocuments();
  }, [regenSignal, currentDocumentsStatus, currentDocuments]);

  // Documents attached on an unmerged branch are absent from the BASE aggregate —
  // fetch each open branch's own documents to surface them. RENEWAL branches stay
  // in RenewalDocumentsCard.
  const openBranchIds = useMemo(
    () =>
      onlyOnBranch || workingBranch
        ? []
        : branches.data
            .filter(
              ({ branchStatus, branchType }) =>
                branchStatus === 'BRANCH_OPEN' && branchType !== 'BASE' && branchType !== 'RENEWAL',
            )
            .map(({ id }) => id),
    [branches, onlyOnBranch, workingBranch],
  );

  const branchDocumentQueries = useQueries({
    queries: openBranchIds.map(branchId => ({
      ...policyControllerListPolicyDocumentsQueryOptions(policy.id, {
        branchId,
        onlyOnBranch: true,
      }),
      select: selectDocumentsFromAPI,
      placeholderData: keepPreviousData,
    })),
  });

  const { data: archivedDocuments, status: archivedDocumentsStatus } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policy.id, {
      branchId: workingBranch?.id,
      archived: true,
      // Please note that the API is not working as expected : archived filter is not working.
      // The API handles only presence or absence of value. If the archived param is not provided,
      // it is considered "false" by default, otherwise it is considered "true".
    }),
    select: selectDocumentsFromAPI,
    placeholderData: keepPreviousData,
  });

  const { data: publicConfig, status: publicConfigStatus } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
  });

  const nonDelegatedDocumentCategories = useMemo(
    () =>
      (publicConfig?.nonDelegatedConfig?.documents?.documentTypes ?? []).map(category => ({
        category,
      })),
    [publicConfig?.nonDelegatedConfig?.documents?.documentTypes],
  );
  const getClassificationCategories = useCallback(
    ({ type }: Pick<PolicyDocumentsDto, 'type'>): DocumentCustomCategorieMapping[] | undefined => {
      if (type === 'non_delegated') return nonDelegatedDocumentCategories;
      if (type === 'supporting_document' && displayContext === 'policyholder') {
        return publicConfig?.policyholderDocuments as DocumentCustomCategorieMapping[] | undefined;
      }
      return undefined;
    },
    [displayContext, nonDelegatedDocumentCategories, publicConfig?.policyholderDocuments],
  );

  const sortedDocumentConfigs = useMemo(() => {
    const commonDocumentTypes = publicConfig?.commonDocuments.map(({ type }) => type) ?? [];
    const presentDocumentTypes = includeUnconfiguredDocuments
      ? [currentDocuments, showArchivedDocuments ? archivedDocuments : []]
          .flat()
          .map(document => document?.category)
          .filter((category): category is string => Boolean(category))
      : [];
    const visibleDocumentTypes = Array.from(new Set([...documentConfigs, ...presentDocumentTypes]))
      .filter(category => !categories || categories.includes(category))
      .filter(category => !excludedCategories?.includes(category) && category !== 'SEPA_MANDATE');

    return sortBy(visibleDocumentTypes, category =>
      DOCUMENT_GROUPS.indexOf(getDocumentGroup(category, commonDocumentTypes)),
    );
  }, [
    archivedDocuments,
    categories,
    currentDocuments,
    documentConfigs,
    excludedCategories,
    includeUnconfiguredDocuments,
    publicConfig?.commonDocuments,
    showArchivedDocuments,
  ]);

  const orderedDocuments = useMemo(() => {
    if (
      currentDocumentsStatus !== 'success' ||
      archivedDocumentsStatus !== 'success' ||
      publicConfigStatus !== 'success'
    ) {
      return [];
    }

    const allDocuments = showArchivedDocuments
      ? [currentDocuments, archivedDocuments].flat()
      : [...currentDocuments];

    const seenIds = new Set(allDocuments.map(document => document?.id));
    for (const { data } of branchDocumentQueries) {
      for (const document of data ?? []) {
        if (!seenIds.has(document.id)) {
          seenIds.add(document.id);
          allDocuments.push(document);
        }
      }
    }

    const filteredDocuments = allDocuments.filter(document =>
      sortedDocumentConfigs.includes(document.category ?? ''),
    );

    return sortBy(filteredDocuments, document =>
      sortedDocumentConfigs.indexOf(document.category ?? ''),
    );
  }, [
    currentDocuments,
    archivedDocuments,
    branchDocumentQueries,
    showArchivedDocuments,
    sortedDocumentConfigs,
    currentDocumentsStatus,
    archivedDocumentsStatus,
    publicConfigStatus,
  ]);

  const displayedDocuments = useMemo(
    () =>
      orderedDocuments.filter(
        ({ type, category }) => type !== 'generated' || category !== 'INVOICE',
      ),
    [orderedDocuments],
  );

  const commonDocumentTypes = publicConfig?.commonDocuments.map(({ type }) => type) ?? [];
  const groupedDocuments = getDocumentGroups({
    documents: displayedDocuments,
    commonDocumentTypes,
    generatedDocumentsDisplay,
  });
  const documentsInDisplayOrder = groupedDocuments.flatMap(({ documents }) => documents);
  const { selectedDocument, viewableDocuments, selectDocument, closeViewer } =
    useDocumentViewer(documentsInDisplayOrder);
  const documentGroups = groupedDocuments.map(group => ({
    ...group,
    label: t(`documentsListsPolicy.groups.${group.id}`),
    documents: group.documents.map(document => ({
      ...document,
      displayLabel: getDocumentCategoryLabel(
        document.category ?? '',
        publicConfig?.policyholderDocuments,
        t,
      ),
    })),
  }));

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const extractionEnabled = useFeatureFlag('perm_ai_extraction', policy.productId);
  const complianceEnabled = useFeatureFlag('perm_ai_validity', policy.productId);
  const coherenceEnabled =
    useFeatureFlag('ops_ai_coherence', policy.productId) && displayContext === 'broker';

  const { data: extractions } = useQuery({
    ...policyDocumentExtractionControllerGetPolicyExtractionsQueryOptions(policy.id, {
      branchId: workingBranch?.id,
    }),
    enabled: displayContext === 'broker' && extractionEnabled,
    refetchInterval: query => {
      const data = query.state.data;
      if (!data?.extractions) return false;
      const hasPending = data.extractions.some(e => e.status === 'PENDING');
      return hasPending ? 2000 : false;
    },
  });

  const { data: validities } = useQuery({
    ...documentValidityControllerGetPolicyValidityQueryOptions(policy.id, {
      branchId: workingBranch?.id,
    }),
    enabled: displayContext === 'broker' && complianceEnabled,
    refetchInterval: query => {
      const data = query.state.data;
      if (!data?.validities) return false;
      const hasPending = data.validities.some(c => c.status === 'PENDING');
      return hasPending ? 2000 : false;
    },
  });

  const { data: coherence } = useQuery({
    ...documentCoherenceControllerGetDocumentCoherenceQueryOptions(policy.id, {
      branchId: workingBranch?.id,
    }),
    enabled: coherenceEnabled,
    refetchInterval: query =>
      query.state.data?.documents.some(({ status }) => status === 'PENDING') ? 2000 : false,
  });

  const getExtractionForDocument = useCallback(
    (documentId: string): GetDocumentExtractionOutput | undefined => {
      return extractions?.extractions?.find(e => e.documentId === documentId);
    },
    [extractions],
  );

  const validityByDocumentId = useMemo(
    () =>
      new Map(
        (validities?.validities ?? [])
          .filter(c => c.status !== 'PENDING')
          .map(c => [c.documentId, c as AIValidityResult]),
      ),
    [validities],
  );

  const selectedDocumentExtraction = selectedDocument
    ? getExtractionForDocument(selectedDocument.id)
    : undefined;

  const selectedDocumentValidity = selectedDocument
    ? validityByDocumentId.get(selectedDocument.id)
    : undefined;

  const { mutate: retryExtraction, isPending: isRetryingExtraction } =
    useDocumentExtractionControllerRetryDocumentExtraction({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            predicate: query => {
              const key = query.queryKey[0];
              return (
                typeof key === 'object' &&
                key !== null &&
                'url' in key &&
                (key as { url: string }).url === '/policies/:policyId/document-extractions'
              );
            },
          });
          toast({ description: t('documents.extraction.retrySuccess') });
        },
        onError: () => {
          toast({
            description: t('documents.extraction.retryError'),
            variant: 'destructive',
          });
        },
      },
    });

  const { mutate: submitFeedback, isPending: isFeedbackPending } =
    useDocumentExtractionControllerSubmitExtractionFeedback({
      mutation: {
        onSuccess: () => {
          toast({ description: t('documents.extraction.feedback.submitted') });
        },
        onError: () => {
          toast({
            description: t('documents.extraction.feedback.error'),
            variant: 'destructive',
          });
        },
      },
    });

  const handleRetryExtraction = useCallback(
    (documentId: string) => {
      retryExtraction({ documentId });
    },
    [retryExtraction],
  );

  const handleExtractionFeedback = useCallback(
    (feedback: SubmitFeedbackBody) => {
      if (!selectedDocument) return;
      submitFeedback({ documentId: selectedDocument.id, data: feedback });
    },
    [selectedDocument, submitFeedback],
  );

  const allDocumentsAccepted = useMemo(
    () =>
      !!conformityByDocumentId &&
      conformityByDocumentId.size > 0 &&
      [...conformityByDocumentId.values()].every(({ decision }) => decision === 'ACCEPTED'),
    [conformityByDocumentId],
  );
  const documentsLoaded =
    currentDocumentsStatus === 'success' &&
    archivedDocumentsStatus === 'success' &&
    publicConfigStatus === 'success';
  const showDocumentsCard =
    !documentsLoaded ||
    documentConfigs.length > 0 ||
    orderedDocuments.length > 0 ||
    Boolean(extraActions);

  const hasHeader = !embedded || controlledShowArchivedDocuments === undefined;
  const archivedToggle = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-pressed={showArchivedDocuments}
      onClick={() => setShowArchivedDocuments(!showArchivedDocuments)}
      className={cn(
        'gap-2 text-muted-foreground hover:text-foreground',
        showArchivedDocuments && 'bg-slate-100 text-foreground',
      )}
    >
      <ArchiveIcon aria-hidden="true" className="h-4 w-4" />
      {t(
        showArchivedDocuments
          ? 'documentsListsPolicy.hideArchived'
          : 'documentsListsPolicy.showArchived',
      )}
    </Button>
  );

  return (
    <>
      <Card
        hidden={!showDocumentsCard}
        className={cn('p-0', embedded && 'border-0 bg-transparent shadow-none')}
      >
        {hasHeader && (
          <CardHeader
            className={cn(
              'flex flex-col gap-3 space-y-0 border-b px-5 py-4 sm:flex-row sm:items-center sm:justify-between',
              embedded && 'py-3',
            )}
          >
            {!embedded && <CardTitle className="min-w-0 flex-1">{title}</CardTitle>}
            {displayContext === 'broker' && (
              <Group wrap className="sm:ml-auto">
                {archivedToggle}
                {Boolean(conformityByDocumentId?.size) && (
                  <ConformityAcceptAllControl
                    conformityByDocumentId={conformityByDocumentId}
                    onAcceptAll={onAcceptAllDocumentConformity}
                  />
                )}
              </Group>
            )}
          </CardHeader>
        )}
        <CardContent className="px-5 pb-5 pt-4">
          {displayContext === 'broker' && !hasHeader && (
            <Group align="stretch" justify="end" className="mb-4">
              {archivedToggle}
            </Group>
          )}
          <Stack gap={displayContext === 'broker' ? 6 : 0}>
            {(currentDocumentsStatus === 'pending' ||
              archivedDocumentsStatus === 'pending' ||
              publicConfigStatus === 'pending') && (
              <Stack gap={2}>
                <Skeleton className="h-[40px] w-full" />
                <Skeleton className="h-[40px] w-full" />
                <Skeleton className="h-[40px] w-full" />
                <Skeleton className="h-[40px] w-full" />
                <Skeleton className="h-[40px] w-full" />
                <Skeleton className="h-[40px] w-full" />
              </Stack>
            )}
            {(currentDocumentsStatus === 'error' ||
              archivedDocumentsStatus === 'error' ||
              publicConfigStatus === 'error') && (
              <Center className="h-full">{t('documentsListsPolicy.fetchError')}</Center>
            )}
            {currentDocumentsStatus === 'success' &&
              archivedDocumentsStatus === 'success' &&
              publicConfigStatus === 'success' && (
                <DocumentsCollection
                  groups={documentGroups}
                  getDocumentLabel={document => document.displayLabel}
                  onView={selectDocument}
                  getClassificationCategories={getClassificationCategories}
                  renderBadges={document => {
                    const conformity = conformityByDocumentId?.get(document.id);
                    const extraction =
                      displayContext === 'broker' && extractionEnabled
                        ? getExtractionForDocument(document.id)
                        : undefined;
                    return (
                      <>
                        {displayContext === 'policyholder' && conformity && (
                          <ConformityReadOnlyBadge conformity={conformity} />
                        )}
                        {extraction && (
                          <AIClassificationBadge
                            extractionData={{
                              status: extraction.status,
                              fields: extraction.fields as Record<string, unknown> | undefined,
                              errorMessage: extraction.errorMessage,
                              onRetry:
                                extraction.status === 'FAILED'
                                  ? () => handleRetryExtraction(document.id)
                                  : undefined,
                              isRetrying: isRetryingExtraction,
                            }}
                          />
                        )}
                      </>
                    );
                  }}
                  renderBelowRow={document => {
                    const conformity = conformityByDocumentId?.get(document.id);
                    if (conformity?.decision !== 'REJECTED') return null;
                    return displayContext === 'policyholder' ? (
                      <ConformityReuploadZone
                        documentId={document.id}
                        reason={conformity.reason}
                        onReupload={onReuploadDocumentConformity}
                      />
                    ) : (
                      <Text size="xs" tone="destructive" className="px-3 pb-2">
                        <Text inline weight="semibold">
                          {t('documentsListsPolicy.conformity.rejectedReasonPrefix')}
                        </Text>{' '}
                        {conformity.reason}
                      </Text>
                    );
                  }}
                />
              )}
          </Stack>
          {extraActions && <div className="mt-4 border-t pt-4">{extraActions}</div>}
        </CardContent>
      </Card>
      {displayContext === 'broker' && <RenewalDocumentsCard />}
      {displayContext === 'policyholder' && allDocumentsAccepted && (
        <PolicyholderCompletionBanner />
      )}

      <DocumentViewer
        productId={policy.productId}
        displayContext={displayContext}
        document={selectedDocument}
        documents={viewableDocuments}
        onClose={closeViewer}
        onDocumentChange={selectDocument}
        documentCustomCategorieMappings={
          selectedDocument ? getClassificationCategories(selectedDocument) : []
        }
        uploadMode={uploadMode}
        extraction={selectedDocumentExtraction ?? null}
        extractionEnabled={extractionEnabled}
        onExtractionFeedback={extractionEnabled ? handleExtractionFeedback : undefined}
        isExtractionFeedbackPending={isFeedbackPending}
        validity={selectedDocumentValidity ?? null}
        validityEnabled={complianceEnabled}
        coherence={
          coherenceEnabled && selectedDocument
            ? coherence?.documents.find(({ documentId }) => documentId === selectedDocument.id)
            : undefined
        }
        conformity={selectedDocument ? conformityByDocumentId?.get(selectedDocument.id) : undefined}
        onAcceptConformity={onAcceptDocumentConformity}
        onRejectConformity={onRejectDocumentConformity}
        onCancelConformity={onCancelDocumentConformity}
      />
    </>
  );
}

export function DocumentsListsPolicyModule(props: DocumentsListsModuleProps) {
  const documentReviewEnabled = useFeatureFlag('feat_document_review');
  return documentReviewEnabled ? (
    <DocumentsListsPolicyModuleV2 {...props} />
  ) : (
    <LegacyDocumentsListsPolicyModule {...props} />
  );
}
